// Sound utility for managing and playing sound effects

const soundCache = {};

/**
 * Preloads a sound file
 * @param {string} soundPath - Path to the sound file
 */
export const preloadSound = (soundPath) => {
  if (!soundPath || soundCache[soundPath]) {
    return;
  }

  try {
    const audio = new Audio(soundPath);
    audio.preload = 'auto';
    soundCache[soundPath] = audio;
  } catch (error) {
    console.warn(`Failed to preload sound: ${soundPath}`, error);
  }
};

/**
 * Plays a sound effect
 * @param {string} soundPath - Path to the sound file
 * @param {number} volume - Volume level (0.0 to 1.0), default 0.5
 * @returns {Promise<void>}
 */
export const playSound = async (soundPath, volume = 0.5) => {
  if (!soundPath) {
    return;
  }

  try {
    // Check if sound is cached
    let audio = soundCache[soundPath];
    
    if (!audio) {
      audio = new Audio(soundPath);
      audio.preload = 'auto';
      soundCache[soundPath] = audio;
    }

    // Clone the audio to allow overlapping sounds
    const audioClone = audio.cloneNode();
    audioClone.volume = Math.max(0, Math.min(1, volume));
    
    // Play the sound
    await audioClone.play();
  } catch (error) {
    // Silently fail for autoplay restrictions or missing files
    // Only log if it's not an autoplay error
    if (error.name !== 'NotAllowedError' && error.name !== 'NotSupportedError') {
      console.warn(`Failed to play sound: ${soundPath}`, error);
    }
  }
};

/**
 * Preloads all common sounds
 */
export const preloadAllSounds = () => {
  const sounds = [
    '/sounds/button-click.mp3',
    '/sounds/button-hover-sound.mp3',
    '/sounds/menu-option-hover.wav',
    '/sounds/interface-selection-hover.wav',
    '/sounds/mouse-hover.wav',
    '/sounds/option-hover-click.wav',
    '/sounds/navigation-click.wav',
    '/sounds/select-option-open.wav'
  ];

  sounds.forEach(sound => preloadSound(sound));
};

